import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { action } from 'mobx'
import { observer } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import * as utif from 'utif2'
// import tiff from '@jimp/tiff'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { FileMap } from './FileMap'
import { DocumentRenderer, OcrImageData } from './IDocumentRenderer'
import {
	ShortcutHandler,
	makeShortcutListener,
} from './KeyboardShortcutHandler'
import { useOcrContext } from './OcrContext'
import { DocumentFormat } from './OcrTypes'
import { PageDisplaySidebar } from './PageDisplaySidebar'
import { ResultsSection } from './ResultsSection'

type CheckOcrCorrectionPage = {
	isSubmitting: boolean
	onSubmit: (
		outcome: string,
		originalValues: Record<string, unknown>,
		obj: unknown,
	) => void
}

export const CheckOcrCorrectionPage = observer(
	({ onSubmit, isSubmitting }: CheckOcrCorrectionPage) => {
		const context = useOcrContext()
		const ocrDocument = context.controller.document
		const styles = useStyles()

		useEffect(() => {
			if (ocrDocument === undefined) throw Error('The document is undefined')

			if (documentType === 'pdf') {
				const url = window.URL.createObjectURL(ocrDocument)

				const promise = new Promise<void>((resolve, reject) => {
					import('pdfjs-dist').then(async (pdfjsLib) => {
						pdfjsLib.GlobalWorkerOptions.workerSrc = await import(
							'pdfjs-dist/build/pdf.worker.entry'
						)

						const loadingTask = pdfjsLib.getDocument(url)
						const pdf = await loadingTask.promise

						const pageCount = pdf.numPages
						initializePageImages(pageCount)

						const renderPdfPage = async (
							pdfDoc: typeof pdf,
							pageNumber: number,
						): Promise<void> => {
							const documentRenderer = context.controller.documentRenderer
							if (documentRenderer === undefined)
								throw new Error(
									`PDF page ${pageNumber} could not be rendered because document renderer is undefined`,
								)

							const page = await pdfDoc.getPage(pageNumber + 1)
							const viewport = page.getViewport({ scale: 3 })

							const canvas = document.createElement('canvas')
							const canvasContext = canvas.getContext('2d')
							if (!canvasContext)
								throw new Error(
									'canvas context for PDF page is null or undefined',
								)

							canvas.height = viewport.height
							canvas.width = viewport.width

							await page.render({
								canvasContext,
								viewport,
							}).promise

							const extent = [0, 0, viewport.width, viewport.height]
							const dataUrl = canvas.toDataURL('image/jpeg', 0.5)

							documentRenderer.renderPage(pageNumber, extent, dataUrl)
						}

						const promises: Promise<void>[] = []
						for (let i = 0; i < pageCount; i++) {
							promises.push(renderPdfPage(pdf, i))
						}
						await Promise.all(promises)
						resolve()
					})
				})
			} else if (documentType === 'tiff') {
				const fileReader = new FileReader()
				fileReader.onload = (e: unknown) => {
					const tiffPages = utif.decode(fileReader.result as ArrayBuffer)

					utif.decodeImage(fileReader.result as ArrayBuffer, tiffPages[0])

					const pageCount = tiffPages.length

					initializePageImages(pageCount)

					for (let i = 0; i < pageCount; i++) {
						renderTiffPage(fileReader.result as ArrayBuffer, tiffPages, i)
					}
				}
				fileReader.readAsArrayBuffer(ocrDocument)
			} else {
				throw new Error(`document type ${documentType} is not supported`)
			}

			const initializePageImages = (pageCount: number) => {
				const images: OcrImageData[] = []
				for (let i = 0; i < pageCount; i++) {
					images.push({
						pageNumber: i,
						rendered: false,
						extent: [0, 0, 0, 0],
						dataUrl: '',
					})
				}
				const documentRenderer = new DocumentRenderer(images)
				context.controller.setDocument(documentRenderer)
			}

			const renderTiffPage = (
				arrayBuffer: ArrayBuffer,
				tiffImages: utif.IFD[],
				pageNumber: number,
			) => {
				const documentRenderer = context.controller.documentRenderer
				if (documentRenderer === undefined)
					throw new Error(
						`TIFF page ${pageNumber} could not be rendered because document renderer is undefined`,
					)
				const page = tiffImages[pageNumber]

				utif.decodeImage(arrayBuffer, page)

				const rgba = utif.toRGBA8(page)

				const canvas = document.createElement('canvas')
				canvas.width = page.width
				canvas.height = page.height

				const canvasContext = canvas.getContext('2d')
				if (!canvasContext)
					throw new Error('canvas context for TIFF image is null or undefined')

				// const imgd = canvasContext.createImageData(width, height)
				// for (let i = 0; i < rgba.length; i++) {
				// 	imgd.data[i] = rgba[i]
				// }

				const imageData = new ImageData(
					new Uint8ClampedArray(rgba),
					page.width,
					page.height,
				)
				canvasContext.putImageData(imageData, 0, 0)

				const extent = [0, 0, page.width, page.height]

				const dataUrl = canvas.toDataURL('image/jpeg', 0.5)

				documentRenderer.renderPage(pageNumber, extent, dataUrl)
			}
		}, [ocrDocument])

		// if we get a bad mime type we might want to check it here, later though 😀
		const documentType = useMemo(() => {
			if (ocrDocument === undefined) throw Error('The document is undefined')

			if (ocrDocument.type.endsWith('pdf')) return 'pdf'
			if (ocrDocument.type.endsWith('tiff')) return 'tiff'
			return 'img'
		}, [ocrDocument])

		useEffect(() => {
			const mapControl = context.controller.mapControl
			const resultsControl = context.controller.resultsControl

			const handlers: ShortcutHandler[] = []
			handlers.push(
				{
					requireCtrlKey: true,
					keys: [','],
					handler: action(() => {
						console.log('last page')
						const currentPageNumber = context.controller.currentPageNumber
						if (
							context.controller.documentRenderer === undefined ||
							currentPageNumber === undefined
						)
							return
						if (currentPageNumber > 0)
							context.controller.mapControl.fitToPage?.call(
								null,
								currentPageNumber - 1,
							)

						context.controller.selectedBoundingBox = undefined
					}),
				},
				{
					requireCtrlKey: true,
					keys: ['.'],
					handler: action(() => {
						console.log('next page!')
						const currentPageNumber = context.controller.currentPageNumber
						if (
							context.controller.documentRenderer === undefined ||
							currentPageNumber === undefined
						)
							return
						console.log('past document renderer guard')

						if (
							currentPageNumber <
							context.controller.documentRenderer.pageCount! - 1
						)
							context.controller.mapControl.fitToPage?.call(
								null,
								currentPageNumber + 1,
							)

						context.controller.selectedBoundingBox = undefined
					}),
				},
				{
					requireCtrlKey: true,
					keys: ['='],
					handler: () => {
						if (mapControl.zoomIn) mapControl.zoomIn()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['-'],
					handler: () => {
						if (mapControl.zoomOut) mapControl.zoomOut()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['ArrowUp'],
					handler: () => {
						if (mapControl.panUp) mapControl.panUp()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['ArrowDown'],
					handler: () => {
						if (mapControl.panDown) mapControl.panDown()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['ArrowLeft'],
					handler: () => {
						if (mapControl.panLeft) mapControl.panLeft()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['ArrowRight'],
					handler: () => {
						if (mapControl.panRight) mapControl.panRight()
					},
				},
				{
					requireCtrlKey: true,
					requireShiftKey: true,
					keys: ['Enter'],
					handler: () => {
						if (resultsControl.submit) resultsControl.submit()
					},
				},
				{
					keys: ['Enter'],
					handler: () => {
						if (resultsControl.nextLowConfidenceField)
							resultsControl.nextLowConfidenceField()
					},
				},
				{
					keys: ['Tab'],
					handler: () => {
						if (resultsControl.nextField) resultsControl.nextField()
					},
				},
				{
					requireShiftKey: true,
					keys: ['Enter'],
					handler: () => {
						if (resultsControl.previousField) resultsControl.previousField()
					},
				},
				{
					requireCtrlKey: true,
					keys: ['h'],
					handler: () => {
						if (mapControl.fitToPage) mapControl.fitToPage()
					},
				},
				{
					requireShiftKey: true,
					keys: ['+'],
					handler: () => {
						if (mapControl.showBoundingBox)
							mapControl.showBoundingBox(
								context.controller.selectedBoundingBox,
								context.controller.currentPageNumber,
								true,
							)
					},
				},
			)

			const disposer = makeShortcutListener(window, handlers)

			return () => {
				disposer()
			}
		}, [context])

		if (context.controller === undefined) return <FullscreenSpinner />

		return (
			<Box
				width="100%"
				height="100%"
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				overflow="hidden"
			>
				<Box
					display="flex"
					flexDirection={
						context.controller.invoiceResults.documentFormat ===
						DocumentFormat.Portrait
							? 'row'
							: 'column'
					}
					width="100%"
					height="100%"
				>
					{context.controller.invoiceResults.documentFormat ===
						DocumentFormat.Portrait && <PageDisplaySidebar />}
					<FileMap />
					<ResultsSection isSubmitting={isSubmitting} onSubmit={onSubmit} />
				</Box>
				{/* <AppBar color="default" position="relative">
					<Box
						component={Toolbar}
						display="flex"
						justifyContent="center"
						gap={2}
					>
						<Button
							variant="outlined"
							color="error"
							startIcon={<Flag />}
							// onClick={() => {
							// 	modalService
							// 		.showForm((props) => (
							// 			<FlagDialog
							// 				onCancel={() => props.close({ closeResult: 'cancel' })}
							// 				onConfirm={(reason) =>
							// 					props.close({ closeResult: 'okay', value: reason })
							// 				}
							// 			/>
							// 		))
							// 		.then((v) => {
							// 			// TODO on submit and pass reason with flagged outcome, along w/ values
							// 			if (v.closeResult === 'okay')
							// 				onSubmit(
							// 					'Flagged',
							// 					store.originalOcrValues,
							// 					cellManager.valueObject,
							// 				)
							// 		})

							// 	// onSubmit(
							// 	// 	'Flagged',
							// 	// 	store.originalOcrValues,
							// 	// 	cellManager.valueObject,
							// 	// )
							// }}
						>
							Flag
						</Button>
						<Button
							variant="contained"
							startIcon={<Send />}
							// onClick={() => {
							// 	onSubmit(
							// 		'Done',
							// 		store.originalOcrValues,
							// 		cellManager.valueObject,
							// 	)
							// }}
							disabled={isSubmitting}
						>
							Submit
						</Button>
					</Box>
				</AppBar> */}
			</Box>
		)
	},
)

const useStyles = makeStyles(() => ({
	ocrPage: {
		display: 'flex',
		flexDirection: 'row',
		overflow: 'hidden',
	},
	pageDisplaySidebar: {
		flex: 1,
	},
	fileMap: {
		flex: 3,
	},
	resultsSidebar: {
		flex: 1,
	},
}))
